/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { AttributeSelector } from '@leverege/ui-attributes';
import { ValueCache } from '@leverege/value-cache';
import { Text, Button, Content, PropertyGrid, Pane, Label } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { connect } from 'react-redux';
import I18N from '@leverege/i18n';
import FileUtil from "./file/Util.js";
import Util from "./Utils.js";
import S from './CreateEditForm.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  sort,
  defaultGetAttributes,
  bulkCreateAttributeFilter
} = Util;
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.forms.BulkCreateMapFieldsForm');
const DO_NOT_MAP_ATTRIBUTE = {
  name: '____DO_NOT_MAP____',
  get displayName() {
    return t('doNotMap');
  }
};
class BulkCreateMapFields extends React.Component {
  static propTypes = {
    getAttributes: PropTypes.func,
    contentVariant: PropTypes.string,
    contentHeaderVariant: PropTypes.string,
    contentAreaVariant: PropTypes.string,
    contentFooterVariant: PropTypes.string,
    titleVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    titleIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitleVariant: PropTypes.string,
    bodyVariant: PropTypes.string,
    eventData: PropTypes.any,
    submitText: PropTypes.string,
    backText: PropTypes.string,
    loading: PropTypes.bool
  };
  static defaultProps = {
    getAttributes: defaultGetAttributes,
    contentVariant: 'default',
    contentHeaderVariant: 'formHeader',
    contentAreaVariant: 'formBody',
    contentFooterVariant: 'formButtons',
    titleVariant: 'dialogTitle',
    get title() {
      return t('title');
    },
    get titleIcon() {
      return tIcon('titleIcon', 'fa fa-plus fa-fw');
    },
    subtitleVariant: 'dialogTitle',
    bodyVariant: 'dialogBody|body',
    eventData: null,
    get submitText() {
      return t('submitText');
    },
    get backText() {
      return t('backText');
    },
    loading: false
  };
  constructor(props) {
    super(props);
    this.cache = new ValueCache();
    this.state = {};
  }
  onSubmit = evt => {
    const {
      onSubmit,
      eventData
    } = this.props;
    if (typeof onSubmit === 'function') {
      onSubmit({
        value: this.getFullMapping(),
        data: eventData,
        original: evt
      });
    }
  };
  onCancel = evt => {
    const {
      onBack,
      eventData
    } = this.props;
    if (onBack) onBack({
      data: eventData,
      original: evt
    });
  };
  getAttributes() {
    const {
      getAttributes,
      target
    } = this.props;
    return this.cache.calc((target, getAttributes) => {
      const attrs = getAttributes(target, bulkCreateAttributeFilter) || [];

      // sort them
      attrs.sort(sort);
      return attrs;
    }, target, getAttributes);
  }
  findAttr(header, attributes) {
    return attributes.find(attr => attr.displayName === header);
  }
  getFullMapping() {
    const {
      data
    } = this.props;
    const {
      mapping
    } = this.state;
    const attributes = this.getAttributes();
    const headers = FileUtil.getHeaders(data);
    const nMapping = {
      ...mapping
    };
    headers.forEach(header => {
      if (!nMapping?.[header]) {
        nMapping[header] = this.findAttr(header, attributes);
      }
    });
    Object.entries(nMapping).forEach(([key, val]) => {
      if (val === DO_NOT_MAP_ATTRIBUTE) {
        delete nMapping[key];
      }
    });
    return nMapping;
  }
  onMappingChange = ({
    data,
    value
  }) => {
    const attr = value === DO_NOT_MAP_ATTRIBUTE.name ? DO_NOT_MAP_ATTRIBUTE : this.getAttributes().find(attr => attr.name === value);
    this.setState(state => {
      const {
        mapping
      } = state;
      const nMapping = {
        ...mapping,
        [data]: attr
      };
      return {
        mapping: nMapping
      };
    });
  };
  attrIsRequired(attr) {
    const required = attr.blueprint?.attribute?.hasTag('is:required') || attr.blueprint?.attribute?.hasTag('formValidator:required');
    return required;
  }
  getUnmappedAttrs(attrs, {
    headers,
    mapping
  }) {
    const claimed = Object.values(mapping || {});
    const unclaimed = attrs.filter(attr => !claimed.includes(attr));
    const semiClaimed = headers.map(hdr => mapping?.[hdr] ?? this.findAttr(hdr, unclaimed));
    return attrs.filter(attr => {
      return !claimed.includes(attr) && !semiClaimed.includes(attr);
    });
  }
  getUnmappedRequiredAttrs(attributes, {
    headers,
    mapping
  }) {
    const unmapped = this.getUnmappedAttrs(attributes, {
      headers,
      mapping
    });
    return unmapped.filter(this.attrIsRequired);
  }
  renderMapping() {
    const attributes = this.getAttributes();
    const {
      data,
      relationship: {
        blueprint
      }
    } = this.props;
    const {
      mapping
    } = this.state;
    const headers = FileUtil.getHeaders(data);
    const parsedData = FileUtil.csvToJson(data);
    return /*#__PURE__*/_jsxs(PropertyGrid, {
      children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
        label: /*#__PURE__*/_jsx(Text, {
          variant: "captionSmallBold",
          children: t('csvField', {
            blueprintName: blueprint.name
          })
        }),
        children: /*#__PURE__*/_jsx(Text, {
          variant: "captionSmallBold",
          children: t('blueprintField', {
            blueprintName: blueprint.name
          })
        })
      }), /*#__PURE__*/_jsx(PropertyGrid.Divider, {
        className: S.divider
      }), headers.map(header => {
        const label = header;
        const example = parsedData.find(r => r?.[header] != null)?.[header];
        const attr = mapping?.[header] ?? this.findAttr(header, attributes);
        return /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: /*#__PURE__*/_jsxs(Pane, {
            variant: "bulkCreateMapFieldsEntry",
            className: S.csvField,
            layout: "flex:bulkCreateMapFieldsEntry|colS",
            children: [/*#__PURE__*/_jsx(Label, {
              htmlFor: header,
              variant: "inputTitle",
              children: label
            }), example ? /*#__PURE__*/_jsx(Text, {
              variant: "caption",
              children: t('example', {
                example
              })
            }) : null]
          }),
          children: /*#__PURE__*/_jsxs(Pane, {
            layout: "flex:rowM",
            children: [/*#__PURE__*/_jsx(Text, {
              icon: "lv lv-arrow-right"
            }), /*#__PURE__*/_jsx(AttributeSelector, {
              className: S.selector,
              id: header,
              values: [DO_NOT_MAP_ATTRIBUTE, ...this.getUnmappedAttrs(attributes, {
                headers,
                mapping
              }), ...(attr && attr !== DO_NOT_MAP_ATTRIBUTE ? [attr] : [])],
              value: attr?.name ?? DO_NOT_MAP_ATTRIBUTE.name,
              onChange: this.onMappingChange,
              eventData: header
            })]
          })
        }, header);
      })]
    });
  }
  submitDisabled() {
    const attributes = this.getAttributes();
    const {
      mapping
    } = this.state;
    const {
      data
    } = this.props;
    const headers = FileUtil.getHeaders(data);
    return this.getUnmappedRequiredAttrs(attributes, {
      headers,
      mapping
    })?.length > 0;
  }
  render() {
    const {
      contentVariant,
      contentHeaderVariant,
      contentAreaVariant,
      contentFooterVariant,
      titleVariant,
      title,
      titleIcon,
      subtitleVariant,
      bodyVariant,
      submitText,
      backText,
      loading,
      relationship: {
        blueprint
      }
    } = this.props;
    const attributes = this.getAttributes();
    const {
      data
    } = this.props;
    const {
      mapping
    } = this.state;
    const headers = FileUtil.getHeaders(data);
    const missingFields = this.getUnmappedRequiredAttrs(attributes, {
      headers,
      mapping
    });
    return /*#__PURE__*/_jsxs(Content, {
      className: S.mapFields,
      variant: contentVariant,
      relative: true,
      children: [/*#__PURE__*/_jsx(Content.Header, {
        variant: contentHeaderVariant,
        children: /*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: title,
          icon: titleIcon
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        variant: contentAreaVariant,
        children: /*#__PURE__*/_jsxs(Content, {
          className: S.mapFieldsBody,
          relative: true,
          children: [/*#__PURE__*/_jsxs(Content.Header, {
            layout: "flex:colM",
            className: S.mapFieldsBodyHeader,
            children: [/*#__PURE__*/_jsx(Text, {
              variant: subtitleVariant,
              children: t('subtitle')
            }), /*#__PURE__*/_jsx(Text, {
              variant: bodyVariant,
              children: t('body', {
                blueprintName: blueprint.name,
                blueprintNamePlural: blueprint.namePlural
              })
            }), missingFields.length > 0 ? /*#__PURE__*/_jsx(Pane, {
              className: S.warningContainer,
              variant: "bulkCreateMapFieldsError",
              children: /*#__PURE__*/_jsxs(Text, {
                icon: tIcon('bulkCreateMapFieldsErrorIcon', 'fak fa-lv-info-filled'),
                className: S.warning,
                variant: "bulkCreateMapFieldsError|caption",
                children: [/*#__PURE__*/_jsx("b", {
                  children: t('missing', {
                    missingFields: missingFields.map(attr => attr.displayName)
                  })
                }), t('missingFields', {
                  missingFields: missingFields.map(attr => attr.displayName)
                })]
              })
            }) : /*#__PURE__*/_jsx(Pane, {
              className: S.successContainer,
              variant: "bulkCreateMapFieldsSuccess",
              children: /*#__PURE__*/_jsx(Text, {
                icon: tIcon('bulkCreateMapFieldsSuccessIcon', 'fak fa-lv-success'),
                className: S.success,
                variant: "bulkCreateMapFieldsSuccess|captionBold",
                children: t('success')
              })
            })]
          }), /*#__PURE__*/_jsx(Content.Area, {
            children: this.renderMapping()
          })]
        })
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        variant: contentFooterVariant,
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          type: "button",
          onClick: this.onCancel,
          children: loading ? /*#__PURE__*/_jsx("i", {
            className: "fa fa-spin fa-spinner"
          }) : backText
        }), /*#__PURE__*/_jsx(Button, {
          variant: "primary",
          disabled: this.submitDisabled(),
          onClick: this.onSubmit,
          children: loading ? /*#__PURE__*/_jsx("i", {
            className: "fa fa-spin fa-spinner"
          }) : submitText
        })]
      })]
    });
  }
}
export default connect((state, props) => {
  return {};
})(BulkCreateMapFields);